//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';

import mediaPlaceholderImage from '@images/media-placeholder.svg';
import { isDefined } from '@utils/filterPredicates';

interface ImageWithErrorHandlingProps
  extends React.ComponentPropsWithoutRef<'img'> {
  placeholderImage?: React.ComponentPropsWithoutRef<'img'>['src'];
  alt: string;
}

/**
 * Displays an image from the provided `src` with fallback to a placeholder image.
 *
 * - If no `src` is passed, it renders the `placeholderImage`.
 * - If a `src` is passed, it attempts to load it offscreen:
 *   - If the image loads successfully, it updates to display the new image.
 *   - If the image fails to load, it continues to display the `placeholderImage`.
 * - If the `src` is updated, it keeps displaying the existing image (previous successful `src` or placeholder)
 *   until the new `src` is successfully loaded or fails to load. It then updates accordingly.
 *
 * This ensures that users never see a broken image or a flash of an image loading.
 */
export const ImageWithErrorHandling = ({
  placeholderImage = mediaPlaceholderImage,
  src,
  ...props
}: ImageWithErrorHandlingProps) => {
  const [displayedSrc, setDisplayedSrc] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!isDefined(src)) {
      setDisplayedSrc(placeholderImage);
      return;
    }

    // Flag to track if the component is still mounted
    let isMounted = true;

    // Create a new Image object to test if the `src` loads successfully
    // This prevents showing a broken image to the user
    const img = new Image();

    // If the image loads successfully, update `displayedSrc` to the actual `src`
    img.onload = () => {
      if (isMounted) {
        setDisplayedSrc(src);
      }
    };
    // If there's an error loading the image, fallback to the `placeholderImage`
    img.onerror = () => {
      if (isMounted) {
        setDisplayedSrc(placeholderImage);
      }
    };
    img.src = src;

    // Cleanup function to prevent state updates if the component unmounts
    return () => {
      isMounted = false;
    };
  }, [src, placeholderImage]);

  return <img {...props} src={displayedSrc} />;
};
